import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './useLanguage';

// Étapes de la démo guidée
export type DemoStep =
    | 'IDLE'                    // Pas encore commencé
    | 'WELCOME'                 // Message de bienvenue (5s après login)
    | 'OPEN_EXPLORER'           // Ouvrir l'explorateur
    | 'OPEN_FILE'               // Ouvrir un fichier
    | 'OPEN_BOARD'              // Ouvrir le tableau
    | 'DROP_FILE_1'             // Glisser premier fichier
    | 'DROP_FILES_2_3'          // Glisser 2 autres fichiers
    | 'CREATE_LINK'             // Créer un lien
    | 'ANNOTATE_LINK'           // Annoter le lien
    | 'CREATE_LINK_2'           // Créer un 2e lien
    | 'DROP_IMAGES'             // Glisser des images
    | 'OPTICAL_ENHANCEMENT'     // Utiliser amélioration optique
    | 'SUPERPOSE_IMAGE'         // Superposer une image
    | 'DEMO_COMPLETE'           // Démo terminée
    | 'FINISH';

/**
 * Hook pour gérer la démo guidée d'ALBION OS
 * Envoie des messages progressifs pour guider le joueur
 */
export const useTutorial = () => {
    const { t } = useLanguage();
    const [step, setStep] = useState<DemoStep>(() => {
        const saved = localStorage.getItem('ALBION_SAVE_DATA');
        if (saved) {
            try {
                const data = JSON.parse(saved);
                if (data.demoStep) return data.demoStep as DemoStep;
            } catch (e) { }
        }
        return 'IDLE';
    });

    // Sauvegarder l'étape actuelle
    const advanceStep = useCallback((next: DemoStep) => {
        console.log('useTutorial - advanceStep:', next);
        setStep(next);
        const saved = localStorage.getItem('ALBION_SAVE_DATA');
        let data = saved ? JSON.parse(saved) : {};
        data.demoStep = next;
        localStorage.setItem('ALBION_SAVE_DATA', JSON.stringify(data));
    }, []);

    // Fonction pour envoyer des messages
    const sendMessage = useCallback((sender: string, text: string, messageKey?: string) => {
        console.log('useTutorial - sendMessage appelé:', sender, messageKey);
        
        // Envoyer le message au messenger
        window.dispatchEvent(new CustomEvent('CHAT_MESSAGE', {
            detail: { 
                sender, 
                text, 
                messageId: messageKey || `demo_${Date.now()}` 
            }
        }));
        
        // Afficher une notification de nouveau message
        window.dispatchEvent(new CustomEvent('NEW_MESSAGE_NOTIFICATION', {
            detail: {
                sender,
                preview: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
                messageId: messageKey || `demo_${Date.now()}`
            }
        }));
    }, []);

    // Écouter l'événement de démarrage de la démo depuis UchroniaOS
    useEffect(() => {
        const handleDemoStart = () => {
            console.log('useTutorial - Événement DEMO_START reçu, step actuel:', step);
            
            // Vérifier que le step est toujours IDLE et qu'on n'a pas déjà envoyé le message
            if (step === 'IDLE') {
                const saved = localStorage.getItem('ALBION_SAVE_DATA');
                let currentStep = 'IDLE';
                if (saved) {
                    try {
                        const data = JSON.parse(saved);
                        currentStep = data.demoStep || 'IDLE';
                    } catch (e) {}
                }
                
                // Double vérification : si le step sauvegardé n'est plus IDLE, ne rien faire
                if (currentStep !== 'IDLE') {
                    console.log('useTutorial - Message déjà envoyé (step sauvegardé:', currentStep, ')');
                    return;
                }
                
                console.log('useTutorial - Envoi du message de bienvenue');
                
                // Utiliser la traduction du message de bienvenue
                sendMessage(
                    'INSTRUCTEUR',
                    t('messenger.demo.welcome'),
                    'demo_welcome'
                );
                
                advanceStep('WELCOME');
            } else {
                console.log('useTutorial - Message déjà envoyé (step actuel:', step, ')');
            }
        };

        window.addEventListener('DEMO_START', handleDemoStart as EventListener);
        
        return () => {
            window.removeEventListener('DEMO_START', handleDemoStart as EventListener);
        };
    }, [step, sendMessage, advanceStep, t]);

    // Écouter l'ouverture de l'explorateur de fichiers
    useEffect(() => {
        const handleWindowOpen = (e: CustomEvent) => {
            const windowId = e.detail?.windowId;
            console.log('useTutorial - Fenêtre ouverte:', windowId, 'step:', step);
            
            if (step === 'WELCOME' && windowId === 'explorer') {
                console.log('useTutorial - Explorateur ouvert, envoi du message');
                sendMessage(
                    'INSTRUCTEUR',
                    t('messenger.demo.explorerOpened'),
                    'demo_explorer'
                );
                advanceStep('OPEN_EXPLORER');
            }
        };

        window.addEventListener('WINDOW_OPENED', handleWindowOpen as EventListener);
        
        return () => {
            window.removeEventListener('WINDOW_OPENED', handleWindowOpen as EventListener);
        };
    }, [step, sendMessage, advanceStep, t]);

    // Écouter l'ouverture d'un fichier
    useEffect(() => {
        const handleFileOpen = (e: CustomEvent) => {
            console.log('useTutorial - Fichier ouvert, step:', step);
            
            if (step === 'OPEN_EXPLORER') {
                console.log('useTutorial - Premier fichier ouvert, envoi du message');
                sendMessage(
                    'INSTRUCTEUR',
                    t('messenger.demo.fileOpened'),
                    'demo_file'
                );
                advanceStep('OPEN_FILE');
            }
        };

        window.addEventListener('FILE_OPENED', handleFileOpen as EventListener);
        
        return () => {
            window.removeEventListener('FILE_OPENED', handleFileOpen as EventListener);
        };
    }, [step, sendMessage, advanceStep, t]);

    // Écouter l'ouverture du tableau
    useEffect(() => {
        const handleWindowOpen = (e: CustomEvent) => {
            const windowId = e.detail?.windowId;
            
            if (step === 'OPEN_FILE' && windowId === 'board') {
                console.log('useTutorial - Tableau ouvert, envoi du message');
                sendMessage(
                    'INSTRUCTEUR',
                    t('messenger.demo.boardOpened'),
                    'demo_board'
                );
                advanceStep('OPEN_BOARD');
            }
        };

        window.addEventListener('WINDOW_OPENED', handleWindowOpen as EventListener);
        
        return () => {
            window.removeEventListener('WINDOW_OPENED', handleWindowOpen as EventListener);
        };
    }, [step, sendMessage, advanceStep, t]);

    // Écouter le drop de fichiers sur le tableau
    useEffect(() => {
        const handleFileDrop = (e: CustomEvent) => {
            const count = e.detail?.count || 1;
            console.log('useTutorial - Fichiers droppés sur le tableau:', count, 'step:', step);
            
            if (step === 'OPEN_BOARD' && count >= 2) {
                console.log('useTutorial - Au moins 2 fichiers droppés, envoi du message');
                sendMessage(
                    'INSTRUCTEUR',
                    t('messenger.demo.filesDropped'),
                    'demo_files_dropped'
                );
                advanceStep('DROP_FILE_1');
            }
        };

        window.addEventListener('BOARD_FILES_DROPPED', handleFileDrop as EventListener);
        
        return () => {
            window.removeEventListener('BOARD_FILES_DROPPED', handleFileDrop as EventListener);
        };
    }, [step, sendMessage, advanceStep, t]);

    // Écouter la création d'un lien
    useEffect(() => {
        const handleLinkCreated = () => {
            console.log('useTutorial - Lien créé, step:', step);
            
            if (step === 'DROP_FILE_1') {
                console.log('useTutorial - Premier lien créé, envoi du message');
                sendMessage(
                    'INSTRUCTEUR',
                    t('messenger.demo.linkCreated'),
                    'demo_link'
                );
                advanceStep('CREATE_LINK');
            }
        };

        window.addEventListener('BOARD_LINK_CREATED', handleLinkCreated as EventListener);
        
        return () => {
            window.removeEventListener('BOARD_LINK_CREATED', handleLinkCreated as EventListener);
        };
    }, [step, sendMessage, advanceStep, t]);

    // Écouter l'utilisation de l'amélioration optique
    useEffect(() => {
        const handleEnhancerUsed = () => {
            console.log('useTutorial - IMAGE_ENHANCER_OPENED reçu, step state actuel:', step);
            
            // Vérifier le step dans localStorage (plus fiable que le state React)
            const saved = localStorage.getItem('ALBION_SAVE_DATA');
            let currentStep = step;
            if (saved) {
                try {
                    const data = JSON.parse(saved);
                    currentStep = data.demoStep || step;
                    console.log('useTutorial - Step dans localStorage:', currentStep);
                } catch (e) {
                    console.error('useTutorial - Erreur lecture localStorage:', e);
                }
            }
            
            if (currentStep === 'CREATE_LINK') {
                console.log('useTutorial - Step correct (CREATE_LINK), envoi du message final');
                sendMessage(
                    'INSTRUCTEUR',
                    t('messenger.demo.enhancerUsed'),
                    'demo_complete'
                );
                advanceStep('DEMO_COMPLETE');
                
                // Le certificat sera maintenant trouvé via les notifications
                // Plus d'ouverture automatique - le joueur peut le découvrir facilement
            } else {
                console.log('useTutorial - Step incorrect pour IMAGE_ENHANCER_OPENED. Step attendu: CREATE_LINK, step actuel:', currentStep);
            }
        };

        window.addEventListener('IMAGE_ENHANCER_OPENED', handleEnhancerUsed as EventListener);
        
        return () => {
            window.removeEventListener('IMAGE_ENHANCER_OPENED', handleEnhancerUsed as EventListener);
        };
    }, [step, sendMessage, advanceStep, t]);

    // Fonction pour envoyer des indices (pour compatibilité)
    const sendRecoveryHint = useCallback((hint: string) => {
        sendMessage('SYSTÈME', hint, `recovery_${Date.now()}`);
    }, [sendMessage]);

    return { 
        step, 
        advanceStep, 
        sendMessage, 
        sendRecoveryHint 
    };
};
